import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {BusinessType} from 'web-angular-client';

import {AlertService} from '../../../sdk/service/alert.service';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {ConstantsUtil} from '../../../sdk/util/constantsUtil';
import {DropdownItem} from '../../../sdk/interface/data/DropdownItem';
import {KycWizardStepForm} from '../kyc-wizard-step-form';
import {KycProfileDetail} from '../model/kyc-profile-detail';

/**
 * Step 1 of the KYC verification wizard: the user/business profile fields the trimmed eCommerce
 * signup did not capture. Deliberately mirrors the corresponding fields of SignupStep1Component
 * (control names, validators, dropdown options) so a later story can extract shared step
 * components between the signup and KYC wizards.
 */
@Component({
    selector: 'app-kyc-step1',
    templateUrl: './kyc-step1.component.html',
    standalone: false
})
export class KycStep1Component extends KycWizardStepForm<KycProfileDetail> implements OnInit, OnChanges
{
    @Input()
    prefilledProfileDetail: KycProfileDetail;

    @Output()
    profileDetail: EventEmitter<KycProfileDetail>;

    @Output()
    nextPage: EventEmitter<number>;

    readonly businessTypeList: DropdownItem[] = ConstantsUtil.BUSINESS_TYPE_LIST;
    readonly clientBaseList: DropdownItem[] = [
        {value: '0 - 1 000'},
        {value: '1 000 - 5 000'},
        {value: '5 000 - 10 000'},
        {value: '10 000 - 50 000'},
        {value: '50 000 - 100 000'},
        {value: '100 000+'}
    ];
    readonly averageTransactionValueList: DropdownItem[] = [
        {value: 'R0,00 - R250,00'},
        {value: 'R250,00 - R500,00'},
        {value: 'R500,00 - R1 000,00'},
        {value: 'R1 000,00 - R2 500,00'},
        {value: 'R2 500,00+'}
    ];

    constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
        this.profileDetail = new EventEmitter<KycProfileDetail>();
        this.nextPage = new EventEmitter<number>();
    }

    get formControls()
    {
        return this.form.controls;
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        this.loadPrefilledDetails(changes);

        if (this.prefilledProfileDetail)
        {
            this.patchPrefilledDataToForm(this.prefilledProfileDetail);
        }
    }

    ngOnInit(): void
    {
        this.form = this.formBuilder.group({
            businessDisplayName: ['', [Validators.required, Validators.maxLength(30)]],
            clientBase: [null, Validators.required],
            averageTransactionValue: [null, Validators.required],
            businessType: [BusinessType.SOLE_PROPRIETOR, Validators.required]
        });

        this.patchPrefilledDataToForm(this.prefilledProfileDetail);
        this.applyReadOnlyState();
    }

    /**
     * Emits the captured profile details and moves the wizard to the next step. In read-only
     * "View submission" mode the details are not emitted — the disabled form holds no fresh input
     * and emitting it would overwrite the parent wizard's state — so the step only navigates.
     */
    submit(): void
    {
        if (!this.readOnly)
        {
            this.profileDetail.emit(this.getProfileDetailFromForm());
        }
        this.nextPage.emit(2);
    }

    getBusinessDisplayNameTooltip(): string
    {
        return 'Organisation trading name, this will be displayed on invoices.';
    }

    loadPrefilledDetails(changes: SimpleChanges): void
    {
        if (changes['prefilledProfileDetail'] && changes['prefilledProfileDetail'].currentValue)
        {
            this.prefilledProfileDetail = changes['prefilledProfileDetail'].currentValue as KycProfileDetail;
        }
    }

    patchPrefilledDataToForm(prefilledData: KycProfileDetail): void
    {
        if (!this.form || !prefilledData)
        {
            return;
        }

        this.form.patchValue({
            ...prefilledData,
            businessType: prefilledData.businessType ?? BusinessType.SOLE_PROPRIETOR
        });
    }

    /**
     * Builds the step's output model from the form values.
     */
    protected getProfileDetailFromForm(): KycProfileDetail
    {
        return {
            businessDisplayName: this.formControls.businessDisplayName.value,
            clientBase: this.formControls.clientBase.value,
            averageTransactionValue: this.formControls.averageTransactionValue.value,
            businessType: this.formControls.businessType.value
        };
    }
}
